import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  CalendarDaysIcon,
  FireIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface StatItem {
  label: string;
  value: number | string;
  icon: typeof CheckCircleIcon;
  color: string;
  change?: string;
}

interface ProgressStatsProps {
  checkInStreak?: number;
  meetingsThisWeek?: number;
  gratitudeStreak?: number;
  stepProgress?: number;
}

export function ProgressStats({
  checkInStreak = 0,
  meetingsThisWeek = 0,
  gratitudeStreak = 0,
  stepProgress = 1,
}: ProgressStatsProps) {
  const stats: StatItem[] = [
    {
      label: 'Check-in Streak',
      value: checkInStreak,
      icon: FireIcon,
      color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
      change: checkInStreak > 0 ? `${checkInStreak} days` : 'Start today!',
    },
    {
      label: 'Meetings This Week',
      value: meetingsThisWeek,
      icon: CalendarDaysIcon,
      color: 'text-accent-500 bg-accent-50 dark:bg-accent-900/20',
    },
    {
      label: 'Gratitude Streak',
      value: gratitudeStreak,
      icon: CheckCircleIcon,
      color: 'text-primary-500 bg-primary-50 dark:bg-primary-900/20',
      change: gratitudeStreak > 0 ? `${gratitudeStreak} days` : 'Add one now',
    },
    {
      label: 'Current Step',
      value: `Step ${stepProgress}`,
      icon: ChartBarIcon,
      color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
    },
  ];

  return (
    <motion.div
      className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
    >
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          className="bg-white dark:bg-surface-800 rounded-xl p-4 border border-surface-200 dark:border-surface-700"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: 0.1 + index * 0.05 }}
        >
          <div className={`inline-flex p-2 rounded-lg ${stat.color} mb-3`}>
            <stat.icon className="w-5 h-5" />
          </div>
          <div className="text-2xl font-bold text-surface-900 dark:text-white">
            {stat.value}
          </div>
          <div className="text-xs text-surface-500 dark:text-surface-400 mt-1">
            {stat.label}
          </div>
          {stat.change && (
            <div className="text-xs text-surface-400 dark:text-surface-500 mt-1">
              {stat.change}
            </div>
          )}
        </motion.div>
      ))}
    </motion.div>
  );
}
